<template>
  <div :class="[
    'relative group/design-root bg-slate-50 dark:bg-slate-900',
    !isAuthPage ? 'flex size-full min-h-screen flex-col' : ''
  ]" style="font-family: Inter, 'Noto Sans', sans-serif;">
    <!-- Header - hidden on authentication pages -->
    <header v-if="!isAuthPage" class="flex items-center justify-between whitespace-nowrap border-b border-solid border-orange-200 dark:border-orange-800 bg-gradient-to-r from-white to-orange-50 dark:from-slate-800 dark:to-slate-800 px-6 lg:px-10 py-4">
      <div class="app-header-brand flex items-center gap-4">
        <img 
          src="@/assets/images/logo.png" 
          alt="Portinos Logo" 
          class="h-9 w-auto"
        />
        <div class="h-5 w-px bg-orange-300 dark:bg-orange-600"></div>
        <router-link 
          to="/budgets" 
          class="text-slate-900 dark:text-slate-100 text-xl font-semibold hover:text-orange-600 dark:hover:text-orange-400 transition-colors duration-200"
        >
          <span class="text-slate-900 dark:text-slate-100 hover:text-orange-600 dark:hover:text-orange-400">Estimaciones Portinos</span> 
        </router-link>
      </div>
      
      <div class="flex items-center gap-4">
        <!-- Navigation - only show when authenticated -->
        <nav v-if="isAuthenticated" class="hidden md:flex items-center gap-2">
        </nav>

        <!-- User Menu -->
        <div v-if="isAuthenticated" class="flex items-center gap-3">
          <!-- Estimaciones Dropdown -->
          <div class="relative" @mouseleave="closeDropdown">
            <button 
              @click="toggleDropdown"
              @mouseenter="openDropdown"
              class="flex items-center justify-center gap-1 text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-sm font-medium leading-normal px-3 py-2 rounded-lg transition-all duration-200 h-9"
              :class="{ 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 shadow-sm': isEstimationRoute }"
            >
              <span>Estimaciones</span>
              <svg 
                class="w-4 h-4 transition-transform duration-200 flex-shrink-0" 
                :class="{ 'rotate-180': isDropdownOpen }"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
            
            <!-- Dropdown Menu -->
            <div 
              v-if="isDropdownOpen"
              @mouseenter="openDropdown"
              class="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-800 border border-orange-200 dark:border-orange-700 rounded-lg shadow-lg z-50"
            >
              <div class="py-2">
                <router-link 
                  to="/budgets"
                  @click="closeDropdown"
                  class="flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200"
                  :class="{ 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400': $route.path === '/budgets' }"
                >
                  Listado de Estimaciones
                </router-link>
                <router-link 
                  to="/"
                  @click="closeDropdown"
                  class="flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200"
                  :class="{ 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400': $route.path === '/' }"
                >
                  Estimación por Items
                </router-link>
                <router-link 
                  to="/team-assignment"
                  @click="closeDropdown"
                  class="flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200"
                  :class="{ 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400': $route.path === '/team-assignment' }"
                >
                  Estimación por Equipo
                </router-link>
              </div>
            </div>
          </div>
          <!-- Admin Dashboard link - only show for admin users -->
          <router-link 
            v-if="currentUser?.role === 'admin'"
            to="/admin/dashboard"
            class="flex items-center justify-center gap-2 text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-sm font-medium leading-normal px-3 py-2 rounded-lg transition-all duration-200 h-9"
            :class="{ 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 shadow-sm': $route.path.startsWith('/admin') }"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span>Administración</span>
          </router-link>
          <!-- User Info -->
          <div class="hidden sm:flex items-center gap-2 text-sm">
            <div class="flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg h-9">
              <div class="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
              <span class="text-slate-700 dark:text-slate-300 font-medium">{{ currentUser?.name || currentUser?.email }}</span>
              <span v-if="currentUser?.role === 'admin'" class="text-xs bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded flex-shrink-0">
                Admin
              </span>
            </div>
          </div>
          
          <!-- Logout Button -->
          <button 
            @click="handleLogout"
            :disabled="isLoading"
            class="flex items-center justify-center gap-2 text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-sm font-medium leading-normal px-3 py-2 rounded-lg transition-all duration-200 disabled:opacity-50 h-9"
          >
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
            <span v-if="!isLoading">Salir</span>
            <span v-else>...</span>
          </button>
        </div>

        <!-- Login/Register Buttons - only show when not authenticated -->
        <div v-else class="flex items-center gap-2">
          <router-link 
            to="/login"
            class="flex items-center justify-center text-slate-700 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-sm font-medium leading-normal px-4 py-2 rounded-lg transition-all duration-200 h-9"
          >
            Ingresar
          </router-link>
          <router-link 
            to="/register"
            class="flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium leading-normal px-4 py-2 rounded-lg transition-all duration-200 shadow-sm h-9"
          >
            Registrarse
          </router-link>
        </div>

        <!-- Dark Mode Toggle -->
        <DarkModeToggle />
      </div>
    </header>

    <router-view></router-view>
  </div>
</template>

<script>
import { onMounted, computed, ref } from 'vue'
import DarkModeToggle from '@/components/ui/DarkModeToggle.vue'
import { useAuth } from '@/composables/useAuth'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'App',
  components: {
    DarkModeToggle
  },
  setup() {
    const { 
      isAuthenticated, 
      currentUser, 
      isLoading, 
      logout, 
      checkAuthStatus 
    } = useAuth()
    const router = useRouter()
    const route = useRoute()

    // Dropdown state
    const isDropdownOpen = ref(false)
    let dropdownTimeout = null

    // Check if current route is an authentication page or admin page
    const isAuthPage = computed(() => {
      const authRoutes = ['/login', '/register']
      return authRoutes.includes(route.path) || route.path.startsWith('/admin')
    })

    // Check if current route is an estimation-related page
    const isEstimationRoute = computed(() => {
      const estimationRoutes = ['/', '/budgets', '/team-assignment']
      return estimationRoutes.includes(route.path)
    })

    // Dropdown functions
    const openDropdown = () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout)
        dropdownTimeout = null
      }
      isDropdownOpen.value = true
    }

    const closeDropdown = () => {
      dropdownTimeout = setTimeout(() => {
        isDropdownOpen.value = false
      }, 150)
    }

    const toggleDropdown = () => {
      if (isDropdownOpen.value) {
        isDropdownOpen.value = false
      } else {
        openDropdown()
      }
    }

    // Check authentication status on app load
    onMounted(async () => {
      await checkAuthStatus()
    })

    const handleLogout = async () => {
      try {
        await logout()
        router.push('/login')
      } catch (error) {
        console.error('Logout failed:', error)
      }
    }

    return {
      isAuthenticated,
      currentUser,
      isLoading,
      handleLogout,
      isAuthPage,
      isDropdownOpen,
      isEstimationRoute,
      openDropdown,
      closeDropdown,
      toggleDropdown
    }
  }
}
</script>